// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import type { LanguageMetadata } from '../types';

// usage example: t('menu.file.file')
export const en = {
  translation: {
    LocaleSwitcher: {
      ru: 'Русский',
      en: 'English',
      cn: '中国人',
      be: 'Беларускі',
      label: 'Language',
    },
    SignInTelForm: {
      goToLogin: 'Go back to login',
      loginViaEmail: 'Login via email',
      check2FAEmail: '2FA code from email (check SPAM)',
      back: 'Back',
      onlyForRegistered: 'Only for registered users',
      confirm: 'Confirm',
      signIn: 'Sign in',
      check2FACall: 'Enter SMS code',
      enterYourTel: 'Enter your phone',
      codeSent: 'We will send you a code. The code can be sent to your email or SMS.',
      error:
        '{error, select, CredentialsSignin {Invalid username or password} other {Unknown error}}',
      retryCondition: 'You can request the code again in',
      minutes: 'minutes',
      agreement: 'By using the service, I agree to',
      privacyPolicy: 'Personal data processing policy',
      and: 'and',
      terms: 'Terms of Service',
      greetings: 'Welcome back',
      glad_to_see_you_again: 'Glad to see you again',
      verification_required: 'Verification required',
      enter_2fa: 'Please enter your 2FA code',
      login_failed: 'Login failed',
    },
    ErrorCard: {
      headerLabel: 'Oops! Something went wrong!',
      backButtonLabel: 'Back to Login',
    },
    ServerDiscovery: {
      title: 'Server discovery',
      serversToJoin: 'Servers to Join',
      officialServerFor: 'The official server for',
      members: 'Members',
      channels: 'Channels',
    },
    ServerPage: {
      selectChannelToStart: 'Welcome to server, select a channel to start.',
    },
    ServerHeader: {
      invitePeople: 'Invite People',
      serverSettings: 'Server Settings',
      manageMembers: 'Manage Members',
      createChannel: 'Create Channel',
      leaveServer: 'Leave Server',
      deleteServer: 'Delete Server',
      legal: 'Legal',
      privacy: 'Privacy',
      terms: 'Terms',
      companyInformation: 'Company Information',
    },
    ServerMembersSidebar: {
      label: 'Members',
      activity: 'Activity',
      online: 'Online',
      offline: 'Offline',
    },
    ServerLayout: {
      company: 'CYBERKOALA',
      companyForm: 'LLC',
    },
    ServerSidebar: {
      label: 'Channels',
      textChannels: 'Text Channels',
      voiceChannels: 'Voice Channels',
      videoChannels: 'Video Channels',
      streamChannels: 'Stream Channels',
      tribuneChannels: 'Tribune Channels',
      members: 'Members',
    },
    ChannelPage: {
      watching: 'Watching',
    },
    NavigationServerDiscovery: {
      tooltip: 'Server Discovery',
    },
    NavigationTalksy: {
      tooltip: 'Messages',
    },
    ChatMessages: {
      loading: 'Loading messages...',
      somethingWentWrong: 'Something went wrong',
      loadMore: 'Load previous messages',
      typing: '{username} is typing...',
      copied: 'Copied',
      failed_to_copy: 'Failed to copy',
    },
    ChatInput: {
      placeholder: 'Message',
      oneTyping: 'is typing...',
      multipleTyping: 'are typing...',
      othersTyping: 'others are typing...',
      and: 'and',
    },
    ChatItem: {
      audioNotSupported: 'Your browser does not support audio.',
      edited: 'edited',
      save: 'Save',
      escapeToCancelEnterToSave: 'Press ESC to cancel, ENTER to save',
      externalLinkWarning: 'External Link Warning',
      redirectNotControlled:
        'This is an external website not controlled by us. We will scan URL using anti-virus in pop-up. You are about to be redirected to',
      cancel: 'Cancel',
      continue: 'Continue',
      read_by_you: 'Read By You',
      read_by_other: 'Read By Other',
      read: 'read',
      unsupported_file_type: 'Unsupported File Type',
    },
    ChatHeader: {
      updates: 'Latest updates',
      close: 'Close',
    },
    ChatWelcome: {
      welcome: 'Welcome to #',
      start: 'This is the start of the',
      channelName: 'channel.',
      conversation: 'conversation with',
    },
    Toast: {
      erorr: 'Error',
      somethingWentWrong: 'Something went wrong',
    },
    Settings: {
      title: 'Account Settings',
      logout: 'Logout',
      avatar: 'Avatar',
      theme: 'Theme',
      last4Digits: 'SMS Code',
      back: 'Back',
      name: 'Full Name',
      phone: 'Phone',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      enable2FA: 'Enable 2FA',
      requiredFields: 'Marked fields are required',
      completeYourProfile: 'Complete your profile',
      save: 'Save',
      confirm: 'Confirm',
      addPhone: 'Link your phone number for quick login via SMS',
      removalRequest: 'Account removal request',
      deleteAccount: 'Delete account',
      email: 'Email',
      nickname: 'Nickname',
    },
    user_settings: {
      failed_to_update_settings: 'Failed to update settings',
      enter_verification_code: 'Please enter your verification code from SMS',
    },
    MeSidebar: {
      find_or_start_a_conversation: 'Find or start a conversation',
      friends: 'Friends',
      message_requests: 'Message requests',
      boost: 'Boost',
      shop: 'Shop',
      direct_messages: 'Direct messages',
      outgoing_messages: 'Outgoing requests',
      received_conversations: 'Received conversations',
      delete_conversation: 'Delete conversation',
      delete_conversation_confirmation:
        'Are you sure want to delete this conversation? This action cannot be undone.',
    },
    MeSettingsSidebar: {
      back_to_servers: 'Back to servers',
      search: 'Search...',
      nothing_found: 'Nothing found',
      user_settings: 'User Settings',
      my_account: 'My Account',
      devices: 'Devices',
      payment_settings: 'Payment Settings',
      subscriptions: 'Subscriptions',
      app_settings: 'App Settings',
      appearance: 'Appearance',
      voice_n_video: 'Voice & Video',
      notifications: 'Notifications',
      keybinds: 'Keybinds',
      language: 'Language',
      advanced: 'Advanced',
      games_database: 'Games Database',
      legal: 'Legal',
      privacy: 'Privacy',
      terms: 'Terms',
      company: 'Company Information',
      activity_settings: 'Activity Settings',
      activity_privacy: 'Activity Privacy',
      desktop_app_settings_title: 'Desktop App Settings',
      change_password: 'Change password',
    },
    activity_privacy_page: {
      track_playing_games: 'Track Playing Games',
      send_telemetry: 'Send Anonymous Telemetry',
      receive_weekly_emails: 'Recieve Weekly Emails',
      connect_steam: 'Connect Steam',
      share_gaming_activity: 'Share Gaming Activity In Profile',
      show_status_in_private_channels: 'Share current channel status while in Private Channels',
    },
    keyboard_shortcuts: {
      title: 'Keyboard Shortcuts',
      toggle_side_panel: 'Toggle Side Panel',
      show_all: 'Show All',
      toggle_theme: 'Toggle Theme',
      toggle_mic: 'Toggle Microphone',
      toggle_camera: 'Toggle Camera',
      toggle_sound: 'Toggle Sound',
    },
    DeleteUserModal: {
      title: 'Delete Account',
      description: 'This user will be deleted',
      cancel: 'Cancel',
      confirm: 'Delete',
    },
    Notifications: {
      notifications: 'Notifications',
      description: 'Pop-up notifications',
      speaking_notifications: 'Muted Mic Alert',
      speaking_notifications_description:
        "Notify when you're speaking but your microphone is muted or not transmitting to the channel.",
      user_start_playing: 'User start playing',
      user_start_playing_description: 'Notify when members start playing games on server',
    },
    Advanced: {
      streaming_options: 'Streaming Options',
      autorun_on_start: 'Autorun on start',
      autorun_enabled: 'Enabled',
      autorun_disabled: 'Disabled',
    },
    ModeToggle: {
      label: 'Toggle theme',
      dark: 'Dark',
      light: 'Light',
      system: 'System',
    },
    ServerSearch: {
      search: 'Search',
      commandPlaceholder: 'Search all channels and members',
      noResults: 'No results found',
    },
    CreateChannelModal: {
      title: 'Create Channel',
      channelName: 'Channel Name',
      channelNamePlaceholder: 'Enter channel name',
      channelType: 'Channel Type',
      channelTypePlaceholder: 'Select channel type',
      create: 'Create',
      requiredChannelName: 'Channel name is required',
      private: 'Private',
    },
    CreateServerModal: {
      title: 'Customize Your Server',
      description:
        'Give your server a personality with a name and an image. You can always change it later. Image is necessary to create server.',
      serverName: 'Server Name',
      serverNamePlaceholder: 'Enter your server name',
      create: 'Create',
      requiredServerName: 'Server name is required',
      private: 'Private',
    },
    DeleteChannelModal: {
      title: 'Delete Channel',
      description: 'Are you sure you want to delete the channel?',
      permanentDelete: 'will be permanently deleted.',
      cancel: 'Cancel',
      confirm: 'Confirm',
    },
    DeleteMessageModal: {
      title: 'Delete Message',
      description: 'Are you sure you want to delete the message?',
      permanentDelete: 'The message will be permanently deleted.',
      cancel: 'Cancel',
      confirm: 'Confirm',
    },
    DeleteServerModal: {
      title: 'Delete Server',
      description: 'Are you sure you want to delete the server?',
      permanentDelete: 'will be permanently deleted',
      cancel: 'Cancel',
      confirm: 'Confirm',
    },
    EditChannelModal: {
      title: 'Edit Channel',
      name: 'Channel Name',
      placeholder: 'Enter channel name',
      type: 'Channel Type',
      placeholderType: 'Select channel type',
      save: 'Save',
      private: 'Private',
      maxParticipants: 'Max participants',
      maxParticipantsPlaceholder: 'Set limit of participants in channel',
      preview: 'Preview',
    },
    EditServerModal: {
      title: 'Edit Server',
      description:
        'Give your server a personality with a name and an image. You can always change it later.',
      name: 'Server Name',
      placeholder: 'Enter server name',
      save: 'Save',
      private: 'Private',
    },
    InviteModal: {
      title: 'Invite Friends',
      label: 'Server Invite Link',
      generate: 'Generate a new link',
      generated: 'Invite link created',
      error: 'Invite link failed',
      copyShort: 'Copy',
      customCode: 'Set custom code',
      codeHint: '6 symbols with 2-3 uppercase characters',
      useSlug: 'Use slug',
      customCodeDisabled: 'Custom code disabled',
      generating: 'Generating link',
      codeTaken: 'Code Taken',
      copiedShort: 'Copied short',
      copied: 'Copied',
      codeUppercaseError: 'Minimum 2 uppercase characters',
      codeFormatError: 'Invalid code format',
    },
    LeaveFeedbackModal: {
      title: 'Provide Audio/Video Quality Feedback',
      placeholder: 'Write your feedback',
      cancel: 'Cancel',
      send: 'Send Feedback',
    },
    LeaveServerModal: {
      title: 'Leave Server',
      description: 'Are you sure you want to leave',
      cancel: 'Cancel',
      confirm: 'Confirm',
    },
    MembersModal: {
      title: 'Manage Members',
      members: 'Members',
      role: 'Role',
      guest: 'Guest',
      private: 'Private',
      moderator: 'Moderator',
      kick: 'Kick',
    },
    MessageFileModal: {
      title: 'Add an attachment',
      description: 'Send a file as a message',
      send: 'Send',
    },
    ServerEvents: {
      upcoming: 'Events',
      noEvents: 'No events scheduled yet.',
      createEvent: 'Create Event',
      where: 'Channel',
      repeatsDaily: 'Repeats Daily',
      repeatsWeekly: 'Repeat Weekly',
      repeatsMonthly: 'Repeat Monthly',
    },
    ServerEventsPage: {
      eventName: 'Event Name',
      eventNamePlaceholder: 'Enter event name',
      description: 'Description',
      descriptionPlaceholder: 'Enter description',
      channel: 'Channel',
      selectChannelPlaceholder: 'Select channel',
      startDate: 'Start Date',
      endDate: 'End Date',
      create: 'Create',
      update: 'Update',
      recurrence: {
        title: 'Recurrence',
        no_repeat: 'No repeat',
        daily: 'Daily',
        weekly: 'Weekly',
        monthly: 'Monthly',
      },
      repeat_until: 'Repeat until',
    },
    UserButton: {
      onlineStatus: 'Online',
      awayStatus: 'Away',
      dndStatus: 'Do Not Disturb',
      invisibleStatus: 'Invisible',
    },
    UploadFilesForm: {
      fileSizeLimit: 'Total file(s) size should not exceed',
      upgradePlan: 'Upgrade your plan now to upload more files',
      uploadFiles: 'Upload file(s)',
    },
    ResetPage: {
      somethingWentWrong: 'Something went wrong...',
      askForHelp: 'Ask for Help',
      contact: 'Contact Us',
      tryAgain: 'Try Again',
    },
    AppSettings: {
      title: 'Settings',
      theme: 'Theme',
      language: 'Language',
      conferenceVolume: 'Conference Volume',
      notifications: 'Notifications',
      outputDevice: 'Output Device',
      privacy: 'Privacy',
      streamingOptions: 'StreamingOptions',
      simulcast: 'Resolution',
      devices: 'Devices',
      autoconnectVoice: 'Auto-Connect Microphone',
      autoconnectVoiceEnabledHint: 'Mic connects automatically when joining',
      autoconnectVoiceDisabledHint: 'Mic stays off until manually enabled',
      enableAutoconnectVoice: 'Enable auto-connect microphone',
      disableAutoconnectVoice: 'Disable auto-connect microphone',
      experimentalFeature: 'experimental',
      sfxVolume: 'Sound Effects Volume',
      captureSystemAudio: 'Capture System Audio',
      captureSystemAudioDisabledHint: 'Dont capture system audio when screen sharing',
      captureSystemAudioEnabledHint: 'Capture system when screen sharing',
      autoGainControl: 'Auto-Gain Control',
      autoGainControlEnabledHint: 'Automatic microphone gain control enabled',
      autoGainControlDisabledHint: 'Automatic microphone gain control disabled',
      disableAutoGainControl: 'Disable auto-gain control',
      enableAutoGainControl: 'Enable auto-gain control',
      noiseSuppression: 'CENC',
      noiseSuppressionEnabledHint: 'Calls Enhanced Noise Suppression enabled',
      noiseSuppressionDisabledHint: 'Calls Enhanced Noise Suppression disabled',
      disableNoiseSuppression: 'Disable CENC',
      enableNoiseSuppression: 'Enable CENC',
      echoCancellation: 'Echo Cancellation',
      echoCancellationEnabledHint: 'Echo Cancellation Enabled',
      echoCancellationDisabledHint: 'Echo Cancellation Disabled',
      disableEchoCancellation: 'Disable Echo Cancellation',
      enableEchoCancellation: 'Enable Echo Cancellation',
    },
    SimulcastSelector: {
      screenShareOptions: 'Select Screen Share Options (Max 3)',
      limit: 'You can select up to 3 options.',
    },
    NavigationGetApp: {
      tooltip: 'Get App',
    },
    DevicesPage: {
      title: 'Devices',
      name: 'Name',
      browser: 'Browser',
      os: 'OS',
      ip: 'IP',
      lastSeen: 'Last Seen',
      sessions: 'Sessions',
      location: 'Location',
      fetching: 'Loading',
      deleting_devices_not_available:
        'Deleting a device is possible in the OPTIMUM and ADVANCED tariff plans.',
    },
    DragDropFileUpload: {
      dropHere: 'Drop your files here to upload',
      maxFileSize: 'Max file size:',
      uploading: 'Uploading files...',
      complete: 'complete',
      failed_to_process: 'Failed to process',
      could_not_convert: 'Could not convert',
      av_not_supported:
        'AV1 videos are not supported in this environment. Please upload H.264 or HEVC.',
      no_valid_files_after_processing:
        'No valid files to upload after processing. Try to encode with H.264 or HEVC codec.',
      uploaded: 'File uploaded',
      failed_to_upload: 'Failed to upload',
    },
    message_requests: {
      accept: 'Accept DM',
      block: 'Block',
      report: 'Report',
      preview_with: 'Preview DM with',
      requests: 'Message requests',
      no_selection: 'No selection',
    },
    video_conference_preview: {
      waiting: 'No participants yet. Waiting for join...',
      join: 'Join',
    },
    edit_server_modal: {
      current_image: 'Current Image',
    },
    user_context_menu: {
      member: 'Member',
      on_server: 'on server',
      mutual_friends: 'Mutual Friends',
      mutual_servers: 'Mutual Servers',
      in_game: 'In Game',
      playing: 'Playing',
      dm: 'Message',
      role: 'Role',
      guest: 'Guest',
      moderator: 'Moderator',
      kick: 'Kick',
      friend: 'friend',
      not_friend: 'Not friend',
    },
    user_context_menu_extended: {
      view_profile: 'View Profile',
      report_member: 'Report Member',
      copy_user_id: 'Copy User ID',
      invite_to_server: 'Invite to server',
      block: 'Block',
    },
    user_profile: {
      dm: 'Message',
      member_since: 'Member since',
      add_note: 'Add note (visible only to you)',
      add_note_placeholder: 'Click to add a note',
      activity_tab: 'Activity',
      my_activity_tab: 'My Activity',
      mutual_friends_tab: 'Mutual Friends',
      mutual_servers_tab: 'Mutual Servers',
      not_sharing_activity: 'do not sharing activity',
      activity_placeholder:
        'Seems like user practicing digital zen\n make a deep breath and say hello',
      no_mutual_friends: 'No mutual Friends',
      no_mutual_servers: 'No mutual Servers',
      played_last_14_days: 'Played',
      invite_to_server: 'Invite to server',
      no_servers_available: 'No servers available',
      select_a_server: 'Select a server',
      delete_from_friends: 'Delete from friends',
      delete_from_friends_confirmation: 'Are you sure you want to delete this user from friends?',
    },
    friends_page: {
      friends: 'Friends',
      no_friends: 'Add a friend from any server',
      delete_button: 'Delete',
      confirm_delete_title: 'Are you sure want to delete this friend?',
      confirm_delete_message: 'This action cannot be undone.',
      all_friends: 'All Friends',
    },
    games_db_page: {
      games_database: 'Games Database',
      game_not_shown: 'Game not shown',
      no_apps_found: 'No Apps found',
      avail_in_higher_plan: 'Available in OPTIMIM and EXTENDED plans',
    },
    desktop_page: {
      desktop_settings: 'Desktop Settings',
      run_on_startup: 'Run on Startup',
      close_on_x: 'Exit via X close button',
      minimize_to_dock: 'Minimize In Taskbar',
      open_settings: 'Open Settings',
      help: 'Help',
      check_for_updates: 'Check for Updates',
      reload: 'Reload',
      game_tracking: 'Game Tracking',
      telemetry: 'Telemetry',
      loading_sound: 'Loading Sound',
      reset_window_position: 'Reset Window Position',
      telemetry_description:
        'The application collects anonymous usage statistics in accordance with the license terms.',
      telemetry_locked: 'This setting is mandatory and cannot be disabled.',
      some_settings_requires_restart: 'Some settings applies only after application restart',
    },
    activity_card: {
      playing: 'playing',
    },
    invite_page: {
      you_been_invited_to: 'You have been invited to',
      public: 'public',
      private: 'private',
      server: 'server',
      join: 'Join',
      cant_join_server: 'Can not Join Server',
      invite_code_missing: 'Invite code expired or missing',
    },
    participant_tile: {
      toggle_sound_on: 'Toggle Sound On',
      toggle_sound_off: 'Toggle Sound Off',
      unfocus: 'Unfocus',
      focus: 'Focus',
      show_video: 'Show Video',
      hide_video: 'Hide Video',
    },
    source_selector_modal: {
      share_screen_or_window: 'Select screen or window to capture',
      capture_sound: 'Capture sound',
      loading: 'Loading',
      start_sharing: 'Start Sharing',
    },
    incoming_call_modal: {
      title: 'Incoming Call',
      calling_you: 'Calling You',
      decline: 'Decline',
      accept: 'Accept',
    },
    auth: {
      error: 'Error',
      two_fa_required: '2FA required',
      check_email_for_code: 'Check your email for a code.',
      something_went_wrong: 'Something went wrong',
      welcome_back: 'Welcome Back!',
      already_have_account: 'Already Have An Account?',
      sign_in: 'Sign in',
      forgot_password: 'Forgot password?',
      register: 'Register',
      account_verification: 'Account verification',
      back_to_login: 'Back to Login',
      verification_failed: 'Verification failed',
      email_verified: 'Email verified! You can now log in.',
      token_not_found: 'Token not found',
      send_reset_link: 'Send Reset Link',
      password_reset_link_sent: 'Password reset link sent! Check your email (and spam folder)',
      registration_failed: 'Registration failed!',
      registration_successful:
        'Registration successful! Check your email (and spam folder) for verification.',
      create_an_account: 'Create an account',
      work_email: 'Work email',
      phone: 'Phone',
      password: 'Password',
      registering_you_agree_to_our: 'Registering you agree to our',
      public_offer: 'Public Offer',
      and: 'and',
      privacy_policy: 'Privacy Policy',
      password_changed: 'Password changed',
      enter_your_new_password: 'Enter your new password',
      new_password: 'New Password',
      reset_password: 'Reset Password',
      login_by_password: 'Login by password',
      full_name: 'Full Name',
      full_name_placeholder: 'John Doe',
      all_fields_required: 'All fields are required.',
      change_password: 'Change password',
      change_password_header: 'Set new password',
      change_password_description:
        'Login with a password is possible only if you have a linked email address. You can set your email address in ',
      passwords_do_not_match: 'Passwords do not match',
      failed_to_change_password: 'Failed to change password',
      current_password: 'Current password (blank if not set)',
      confirm_new_password: 'Confirm new password',
      password_changed_successfully: 'Password changed successfully',
      saving: 'Saving',
      verification_token: 'Verification token',
      retry_verification: 'Retry',
      too_many_attempts: 'Too many attempts. Try again later',
      attempts_left: 'Attempt left',
      verifying: 'Verifying',
      password_min_length: 'Minimum password length - {{length}} symbols',
      email_required_to_change_password:
        'Please set your email address before changing your password.',
      email_verification_required_to_change_password:
        'Please verify your email address before changing your password.',
      loading_user_data: 'Loading your account information…',
      email_verified_on: '✓ Email verified on {{date}}',
    },
    games_library: {
      title: 'Game Library',
      library: 'Library',
      game_count: '{{count}} games',

      all_games: 'All Games',
      recently_played: 'Recently Played',
      mutual_games: 'Mutual Games',

      hours_played: 'hours played',
      hours_short: 'hrs played',
      minutes_short: 'min played',

      play: 'Play',
      details: 'Details',

      search_placeholder: 'Search games...',

      no_games: 'No games found',
    },
    routes: {
      friends: 'Friends',
      messages: 'Messages',
      discovery: 'Server Discovery',
      voice_video_settings: 'Voice Video Settings',
      account: 'Account',
      message_requests: 'Message Requests',
      desktop_app: 'Desktop App Settings',
      devices: 'Devices',
      subscriptions: 'Subscriptions',
      appearance: 'Appearance',
      notifications: 'Notifications',
      keybinds: 'Keybindings',
      language: 'Language',
      advanced: 'Advanced',
      gamesdb: 'Games Database',
      games_library: 'Games Library',
      activity_privacy: 'Activity Privacy',
      privacy_policy: 'Privacy Policy',
      terms_of_use: 'Terms Of Use',
      change_password: 'Change password',
    },
    time: {
      minutes: 'min',
      hours: 'h',
      days: 'days',
      now: 'now',
      ago: 'ago',
      weeks: 'weeks',
    },
    common: {
      cancel: 'Cancel',
      delete: 'Delete',
      back: 'Back',
      minutes: 'min.',
      loading: 'Loading',
      go_back_to_dms: 'Go Back to DMs',
      drop_here: 'Drop here',
      max_file_size: 'Max file size:',
      processing_video: 'Processing video...',
      uploading: 'Uploading',
      complete: 'Complete',
      something_went_wrong: 'Something went wrong. Please try again later.',
      success: 'Success',
      go_to_server: 'Go to server',
      error: 'Error',
      upgrade_your_account: 'Upgrade your account',
      month: 'month',
      year: 'annual',
      need_help_with_order: 'Need help with order',
      no_products_found: 'No product found',
      available_in_subscription: 'Available only in Subscription',
      session_lifetime: 'Session lifetime',
      action_na: 'Action not available',
      go_to_subscriptions: 'Go to subscriptions',
      search: 'Search',
      online: 'Online',
      offline: 'Offline',
      all: 'All',
      screen: 'screen',
      more_in_desktop_app: 'More in Desktop app',
      starting: 'starting',
      close: 'Close',
      invite: 'Invite',
    },
    // CHARTSDB
    menu: {
      file: {
        file: 'File',
        new: 'New',
        open: 'Open',
        save: 'Save',
        import: 'Import',
        export_sql: 'Export SQL',
        export_as: 'Export as',
        delete_diagram: 'Delete Diagram',
        exit: 'Exit',
      },
      edit: {
        edit: 'Edit',
        undo: 'Undo',
        redo: 'Redo',
        clear: 'Clear',
      },
      view: {
        view: 'View',
        show_sidebar: 'Show Sidebar',
        hide_sidebar: 'Hide Sidebar',
        hide_cardinality: 'Hide Cardinality',
        show_cardinality: 'Show Cardinality',
        zoom_on_scroll: 'Zoom on Scroll',
        theme: 'Theme',
        show_dependencies: 'Show Dependencies',
        hide_dependencies: 'Hide Dependencies',
        show_minimap: 'Show Mini Map',
        hide_minimap: 'Hide Mini Map',
      },
      backup: {
        backup: 'Backup',
        export_diagram: 'Export Diagram',
        restore_diagram: 'Restore Diagram',
      },
      help: {
        help: 'Help',
        docs_website: 'Docs',
        join_discord: 'Join us on Discord',
      },
    },

    delete_diagram_alert: {
      title: 'Delete Diagram',
      description: 'This action cannot be undone. This will permanently delete the diagram.',
      cancel: 'Cancel',
      delete: 'Delete',
    },

    clear_diagram_alert: {
      title: 'Clear Diagram',
      description:
        'This action cannot be undone. This will permanently delete all the data in the diagram.',
      cancel: 'Cancel',
      clear: 'Clear',
    },

    reorder_diagram_alert: {
      title: 'Reorder Diagram',
      description: 'This action will rearrange all tables in the diagram. Do you want to continue?',
      reorder: 'Reorder',
      cancel: 'Cancel',
    },

    multiple_schemas_alert: {
      title: 'Multiple Schemas',
      description:
        '{{schemasCount}} schemas in this diagram. Currently displaying: {{formattedSchemas}}.',
      dont_show_again: "Don't show again",
      change_schema: 'Change',
      none: 'none',
    },

    copy_to_clipboard_toast: {
      unsupported: {
        title: 'Copy failed',
        description: 'Clipboard not supported.',
      },
      failed: {
        title: 'Copy failed',
        description: 'Something went wrong. Please try again.',
      },
    },

    theme: {
      system: 'System',
      light: 'Light',
      dark: 'Dark',
    },

    zoom: {
      on: 'On',
      off: 'Off',
    },

    last_saved: 'Last saved',
    saved: 'Saved',
    loading_diagram: 'Loading diagram...',
    deselect_all: 'Deselect All',
    select_all: 'Select All',
    clear: 'Clear',
    show_more: 'Show More',
    show_less: 'Show Less',
    copy_to_clipboard: 'Copy to Clipboard',
    copied: 'Copied!',

    side_panel: {
      schema: 'Schema:',
      filter_by_schema: 'Filter by schema',
      search_schema: 'Search schema...',
      no_schemas_found: 'No schemas found.',
      view_all_options: 'View all Options...',
      tables_section: {
        tables: 'Tables',
        add_table: 'Add Table',
        filter: 'Filter',
        collapse: 'Collapse All',
        clear: 'Clear Filter',
        no_results: 'No tables found matching your filter.',
        show_list: 'Show Table List',
        show_dbml: 'Show DBML Editor',

        table: {
          fields: 'Fields',
          nullable: 'Nullable?',
          primary_key: 'Primary Key',
          indexes: 'Indexes',
          comments: 'Comments',
          no_comments: 'No comments',
          add_field: 'Add Field',
          add_index: 'Add Index',
          index_select_fields: 'Select fields',
          no_types_found: 'No types found',
          field_name: 'Name',
          field_type: 'Type',
          field_actions: {
            title: 'Field Attributes',
            unique: 'Unique',
            character_length: 'Max Length',
            comments: 'Comments',
            no_comments: 'No comments',
            delete_field: 'Delete Field',
          },
          index_actions: {
            title: 'Index Attributes',
            name: 'Name',
            unique: 'Unique',
            delete_index: 'Delete Index',
          },
          table_actions: {
            title: 'Table Actions',
            change_schema: 'Change Schema',
            add_field: 'Add Field',
            add_index: 'Add Index',
            duplicate_table: 'Duplicate Table',
            delete_table: 'Delete Table',
          },
        },
        empty_state: {
          title: 'No tables',
          description: 'Create a table to get started',
        },
      },
      relationships_section: {
        relationships: 'Relationships',
        filter: 'Filter',
        add_relationship: 'Add Relationship',
        collapse: 'Collapse All',
        relationship: {
          primary: 'Primary Table',
          foreign: 'Referenced Table',
          cardinality: 'Cardinality',
          delete_relationship: 'Delete',
          relationship_actions: {
            title: 'Actions',
            delete_relationship: 'Delete',
          },
        },
        empty_state: {
          title: 'No relationships',
          description: 'Create a relationship to connect tables',
        },
      },
      dependencies_section: {
        dependencies: 'Dependencies',
        filter: 'Filter',
        collapse: 'Collapse All',
        dependency: {
          table: 'Table',
          dependent_table: 'Dependent View',
          delete_dependency: 'Delete',
          dependency_actions: {
            title: 'Actions',
            delete_dependency: 'Delete',
          },
        },
        empty_state: {
          title: 'No dependencies',
          description: 'Create a view to get started',
        },
      },
    },

    toolbar: {
      zoom_in: 'Zoom In',
      zoom_out: 'Zoom Out',
      save: 'Save',
      show_all: 'Show All',
      undo: 'Undo',
      redo: 'Redo',
      reorder_diagram: 'Reorder Diagram',
      highlight_overlapping_tables: 'Highlight Overlapping Tables',
    },

    new_diagram_dialog: {
      database_selection: {
        title: 'What is your Database?',
        description: 'Each database has its own unique features and capabilities.',
        check_examples_long: 'Check Examples',
        check_examples_short: 'Examples',
      },

      import_database: {
        title: 'Import your Database',
        database_edition: 'Database Edition:',
        step_1: 'Run this script in your database:',
        step_2: 'Paste the script result into this modal.',
        script_results_placeholder: 'Script results here...',
        ssms_instructions: {
          button_text: 'SSMS Instructions',
          title: 'Instructions',
          step_1: 'Go to Tools > Options > Query Results > SQL Server.',
          step_2:
            'If you\'re using "Results to Grid," change the Maximum Characters Retrieved for Non-XML data (set to 9999999).',
        },
        instructions_link: 'Need help? Watch how',
        check_script_result: 'Check Script Result',
      },

      cancel: 'Cancel',
      import_from_file: 'Import from File',
      back: 'Back',
      empty_diagram: 'Empty diagram',
      continue: 'Continue',
      import: 'Import',
    },

    open_diagram_dialog: {
      title: 'Open Diagram',
      description: 'Select a diagram to open from the list below.',
      table_columns: {
        name: 'Name',
        created_at: 'Created at',
        last_modified: 'Last modified',
        tables_count: 'Tables',
      },
      cancel: 'Cancel',
      open: 'Open',
    },

    export_sql_dialog: {
      title: 'Export SQL',
      description: 'Export your diagram schema to {{databaseType}} script',
      close: 'Close',
      loading: {
        text: 'AI is generating SQL for {{databaseType}}...',
        description: 'This should take up to 30 seconds.',
      },
      error: {
        message: 'Error generating SQL script. Please try again later or <0>contact us</0>.',
        description: 'Feel free to use your OPENAI_TOKEN, see the manual <0>here</0>.',
      },
    },

    create_relationship_dialog: {
      title: 'Create Relationship',
      primary_table: 'Primary Table',
      primary_field: 'Primary Field',
      referenced_table: 'Referenced Table',
      referenced_field: 'Referenced Field',
      primary_table_placeholder: 'Select table',
      primary_field_placeholder: 'Select field',
      referenced_table_placeholder: 'Select table',
      referenced_field_placeholder: 'Select field',
      no_tables_found: 'No tables found',
      no_fields_found: 'No fields found',
      create: 'Create',
      cancel: 'Cancel',
    },

    import_database_dialog: {
      title: 'Import to Current Diagram',
      override_alert: {
        title: 'Import Database',
        content: {
          alert: 'Importing this diagram will affect existing tables and relationships.',
          new_tables: '<bold>{{newTablesNumber}}</bold> new tables will be added.',
          new_relationships:
            '<bold>{{newRelationshipsNumber}}</bold> new relationships will be created.',
          tables_override: '<bold>{{tablesOverrideNumber}}</bold> tables will be overwritten.',
          proceed: 'Do you want to proceed?',
        },
        import: 'Import',
        cancel: 'Cancel',
      },
    },

    export_image_dialog: {
      title: 'Export Image',
      description: 'Choose the scale factor for export:',
      scale_1x: '1x Regular',
      scale_2x: '2x (Recommended)',
      scale_3x: '3x',
      scale_4x: '4x',
      cancel: 'Cancel',
      export: 'Export',
    },

    new_table_schema_dialog: {
      title: 'Select Schema',
      description: 'Multiple schemas are currently displayed. Select one for the new table.',
      cancel: 'Cancel',
      confirm: 'Confirm',
    },

    update_table_schema_dialog: {
      title: 'Change Schema',
      description: 'Update table "{{tableName}}" schema',
      cancel: 'Cancel',
      confirm: 'Change',
    },

    star_us_dialog: {
      title: 'Help us improve!',
      description: "Would you like to star us on GitHub? It's just a click away!",
      close: 'Not now',
      confirm: 'Of course!',
    },
    export_diagram_dialog: {
      title: 'Export Diagram',
      description: 'Choose the format for export:',
      format_json: 'JSON',
      cancel: 'Cancel',
      export: 'Export',
      error: {
        title: 'Error exporting diagram',
        description: 'Something went wrong. Need help? calls@cyberkoala.ru',
      },
    },

    import_diagram_dialog: {
      title: 'Import Diagram',
      description: 'Import a diagram from a JSON file.',
      cancel: 'Cancel',
      import: 'Import',
      error: {
        title: 'Error importing diagram',
        description:
          'The diagram JSON is invalid. Please check the JSON and try again. Need help?  calls@cyberkoala.ru',
      },
    },

    import_dbml_dialog: {
      example_title: 'Import Example DBML',
      title: 'Import DBML',
      description: 'Import a database schema from DBML format.',
      import: 'Import',
      cancel: 'Cancel',
      skip_and_empty: 'Skip & Empty',
      show_example: 'Show Example',
      error: {
        title: 'Error importing DBML',
        description: 'Failed to parse DBML. Please check the syntax.',
      },
    },
    relationship_type: {
      one_to_one: 'One to One',
      one_to_many: 'One to Many',
      many_to_one: 'Many to One',
      many_to_many: 'Many to Many',
    },

    canvas_context_menu: {
      new_table: 'New Table',
      new_relationship: 'New Relationship',
    },

    table_node_context_menu: {
      edit_table: 'Edit Table',
      duplicate_table: 'Duplicate Table',
      delete_table: 'Delete Table',
      add_relationship: 'Add Relationship',
    },

    snap_to_grid_tooltip: 'Snap to Grid (Hold {{key}})',

    tool_tips: {
      double_click_to_edit: 'Double-click to edit',
    },

    language_select: {
      change_language: 'Language',
    },

    importantUpdateRequired: {
      title: 'Desktop App Update Required',
      description:
        'Support for the old version certificates has ended. Download the new version from ccalls.site',
      download: 'Download',
      close: 'Close',
    },
  },
};

export const enMetadata: LanguageMetadata = {
  name: 'English',
  nativeName: 'English',
  code: 'en',
};
